import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
  App,
  AppFooter,
  AppFooterLink,
  TextLink,
  Type,
} from '@spotify-internal/encore-creator-web';
import {
  black,
  gray20,
  gray60,
  white,
  spacer8,
  spacer16,
  spacer48,
  screenMdMin,
} from '@spotify-internal/encore-foundation';

import { SpotifyLogo } from '../../vendor/SpotifyLogo';
import { TwitterLogo } from '../../vendor/TwitterLogo/index';
import { pageConstraintStyles } from './styles';
import { useIntl, defineMessage, FormattedMessage } from 'react-intl';
import { LOCALE_PREFERENCES } from '../../lib/constants';

export function Footer() {
  const intl = useIntl();
  const localeLegal = LOCALE_PREFERENCES[intl.locale]
    ? LOCALE_PREFERENCES[intl.locale].languageCode
    : 'us';
  const appFooterLinks = [
    {
      label: intl.formatMessage(
        defineMessage({
          id: 'landing-page.footer.terms-conditions',
          defaultMessage: 'Terms & Conditions',
        }),
      ),
      href: `https://www.spotify.com/${localeLegal +
        '/'}legal/spotify-for-podcasters-platform-terms`,
    },
    {
      label: intl.formatMessage(
        defineMessage({
          id: 'landing-page.footer.legal',
          defaultMessage: 'Legal',
        }),
      ),
      href: `https://www.spotify.com/${localeLegal}/legal`,
    },
    {
      label: intl.formatMessage(
        defineMessage({
          id: 'landing-page.footer.privacy-policy',
          defaultMessage: 'Privacy Policy',
        }),
      ),
      href: `https://www.spotify.com/${localeLegal + '/'}legal/privacy-policy/`,
    },
    {
      label: intl.formatMessage(
        defineMessage({
          id: 'landing-page.footer.cookies',
          defaultMessage: 'Cookies',
        }),
      ),
      href: `https://www.spotify.com/${localeLegal + '/'}legal/privacy-policy/#s13`,
    },
  ];

  return (
    <div style={{ backgroundColor: black }}>
      <FooterContent>
        <S4PodsLinks>
          <S4PodsLinksLeft>
            <div style={{ width: '132px', height: '40px' }}>
              <SpotifyLogo />
            </div>
            <div style={{ marginTop: spacer16 }}>
              <Type
                as="p"
                variant={Type.cta3}
                weight={Type.bold}
                style={{ color: gray60, marginBottom: spacer8 }}
              >
                <FormattedMessage
                  id="landing-page.footer.helpful-links"
                  defaultMessage="Helpful links"
                />
              </Type>
              <Type as="p">
                <HelpfulLink
                  href="https://www.spotify.com/us/about-us/contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage id="landing-page.footer.about" defaultMessage="About" />
                </HelpfulLink>
              </Type>
              <Type as="p">
                <HelpfulLink
                  href="https://newsroom.spotify.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage
                    id="landing-page.footer.press-media"
                    defaultMessage="Press & Media"
                  />
                </HelpfulLink>
              </Type>
              <Type as="p">
                <HelpfulLink href="/faq#how-can-i-contact-the-spotify-for-podcasters-team">
                  <FormattedMessage
                    id="landing-page.footer.contact-us"
                    defaultMessage="Contact Us"
                  />
                </HelpfulLink>
              </Type>
            </div>
          </S4PodsLinksLeft>
          <a
            href="https://twitter.com/forpodcasters"
            target="_blank"
            rel="noopener noreferrer"
            style={{ backgroundColor: gray20, padding: '15px', borderRadius: '30px' }}
          >
            <div style={{ width: '24px', height: '24px' }}>
              <TwitterLogo />
            </div>
          </a>
        </S4PodsLinks>
        <ThemeProvider theme={App.darkTheme}>
          <AppFooter
            copyrightYear={new Date().getFullYear()}
            list={appFooterLinks.map(link => (
              <AppFooterLink
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
              </AppFooterLink>
            ))}
            style={{ border: 0 }}
          />
        </ThemeProvider>
      </FooterContent>
    </div>
  );
}

const FooterContent = styled.div`
  ${pageConstraintStyles};

  padding: 80px ${spacer48} 60px;
`;

const S4PodsLinks = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 80px;
`;

const S4PodsLinksLeft = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${spacer16};

  @media (min-width: ${screenMdMin}) {
    grid-template-columns: auto auto;
    grid-column-gap: ${spacer48};
  }
`;

const HelpfulLink = styled(TextLink)`
  color: ${white};
  font-size: 16px;
  margin-bottom: 6px;

  &:focus:not([disabled]) {
    color: ${white};
  }
  &:active:not([disabled]) {
    color: ${white};
  }
`;
